import { WebViewerInstance } from '@pdftron/webviewer';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { MetricsEventNames, MetricsEventOrigin } from 'api/metrics/events';
import { Loading } from 'components/atoms/loading';
import { ComparisonFooter } from 'components/organisms/image-preview/comparison-footer';
import { ImagePreview } from 'components/organisms/image-preview/image-preview';
import { ComparisionPreviewHeader } from 'components/organisms/image-preview/preview-header/comparision-preview-header';
import { RoutePaths } from 'constant';
import { useMetricEventsMutation } from 'queries/metric-events';
import { useAttachmentDetails } from '../use-attachment-details';
import { WorkspaceWrapper } from '../workspace';
import { ComparisonComments } from './comparison-comments';
import {
  ComaparisonModeWrapper,
  ComaparisonWrapper,
  CommentsSection,
  DividerBar,
  LoadingContainer,
  Wrapper,
} from './comparison-styled-components';

export const SideBySideComapre = () => {
  const match = useRouteMatch([RoutePaths.attachmentWorkspaceSideBySide]);
  const { attachmentId, compareAttachmentId } = match?.params as {
    attachmentId: number;
    compareAttachmentId: number;
  };
  const {
    latestVersion,
    versionFileList,
    leftAttachment,
    setLeftAttachment,
    rightAttachment,
    setRightAttachment,
    isChatSectionComparisonVisible,
    setChatSectionComparisonVisiblity,
    onOpenParentTask,
  } = useAttachmentDetails(attachmentId, compareAttachmentId);

  const [isAttachmentLinked, setIsAttachmentLinked] = useState(false);
  const [selectedInsatnce] = useState<WebViewerInstance | null>(null);
  const [discussionAttachmentId, setDiscussionAttachmentId] = useState(
    leftAttachment?.attachmentId,
  );
  const [showResolvedCommentsLeft, setShowResolvedCommentsLeft] =
    useState(false);
  const [showResolvedCommentsRight, setShowResolvedCommentsRight] =
    useState(false);
  const { mutateAsync: comparisionModeEntered } = useMetricEventsMutation({
    originId: latestVersion?.attachmentId,
    originType: MetricsEventOrigin.Attachment,
    eventName: MetricsEventNames.EnteredAttachmentComparisonMode,
  });

  const { mutateAsync: attachmentComparisionLinked } = useMetricEventsMutation({
    originId: latestVersion?.attachmentId,
    originType: MetricsEventOrigin.Attachment,
    eventName: MetricsEventNames.AttachmentComparisionLinked,
  });

  const isLeftAttachment = useMemo(
    () => discussionAttachmentId === Number(attachmentId),
    [attachmentId, discussionAttachmentId],
  );

  useEffect(() => {
    if (latestVersion?.attachmentId) {
      comparisionModeEntered();
    }
  }, [comparisionModeEntered, latestVersion?.attachmentId]);

  useEffect(() => {
    if (isAttachmentLinked) {
      attachmentComparisionLinked();
    }
  }, [attachmentComparisionLinked, isAttachmentLinked]);

  useEffect(() => {
    if (leftAttachment) {
      setDiscussionAttachmentId(leftAttachment.attachmentId);
    }
  }, [leftAttachment]);

  if (leftAttachment && rightAttachment && latestVersion) {
    return (
      <WorkspaceWrapper>
        <ComparisionPreviewHeader
          latestVersionId={latestVersion?.attachmentId}
          leftAttachmentId={leftAttachment.attachmentId}
          rightAttachmentId={rightAttachment.attachmentId}
          isChatSectionComparisonVisible={isChatSectionComparisonVisible}
          setChatSectionComparisonVisiblityToggle={() =>
            setChatSectionComparisonVisiblity((value) => !value)
          }
          latestVersion={latestVersion}
          onOpenParentTask={onOpenParentTask}
        />
        <ComaparisonModeWrapper>
          <Wrapper>
            <ComaparisonWrapper>
              <ImagePreview
                attachmentId={leftAttachment.attachmentId}
                data={leftAttachment}
                setDiscussionAttachmentId={(value) =>
                  setDiscussionAttachmentId(value)
                }
                previewConfig={{
                  zoomTogether: isAttachmentLinked,
                  rotateTogether: isAttachmentLinked,
                  scrollTogether: isAttachmentLinked,
                  isSideBySide: true,
                }}
                isLeftAttachment
                src={leftAttachment.location}
                showResolvedCommentsCompare={showResolvedCommentsLeft}
                setShowResolvedCommentsComapre={(value: boolean) =>
                  setShowResolvedCommentsLeft(value)
                }
              />
              <DividerBar>
                <div />
              </DividerBar>
              <ImagePreview
                attachmentId={rightAttachment.attachmentId}
                data={rightAttachment}
                isRightAttachment
                setDiscussionAttachmentId={(value) =>
                  setDiscussionAttachmentId(value)
                }
                previewConfig={{
                  zoomTogether: isAttachmentLinked,
                  rotateTogether: isAttachmentLinked,
                  scrollTogether: isAttachmentLinked,
                  isSideBySide: true,
                }}
                src={rightAttachment.location}
                showResolvedCommentsCompare={showResolvedCommentsRight}
                setShowResolvedCommentsComapre={(value: boolean) =>
                  setShowResolvedCommentsRight(value)
                }
              />
            </ComaparisonWrapper>
            <ComparisonFooter
              isOverlay={false}
              attachmentList={versionFileList}
              leftAttachment={leftAttachment}
              setLeftAttachment={setLeftAttachment}
              rightAttachment={rightAttachment}
              setRightAttachment={setRightAttachment}
              isAttachmentLinked={isAttachmentLinked}
              toggleAttachmentLink={() =>
                setIsAttachmentLinked((isLinked) => !isLinked)
              }
            />
          </Wrapper>
          <CommentsSection isClose={!isChatSectionComparisonVisible}>
            <ComparisonComments
              versionFileList={versionFileList}
              leftAttachment={leftAttachment}
              rightAttachment={rightAttachment}
              selectedInsatnce={selectedInsatnce}
              discussionAttachmentId={discussionAttachmentId}
              setDiscussionAttachmentId={(value) =>
                setDiscussionAttachmentId(value)
              }
              showResolvedComments={
                isLeftAttachment
                  ? showResolvedCommentsLeft
                  : showResolvedCommentsRight
              }
              toggleShowResolvedComments={() =>
                isLeftAttachment
                  ? setShowResolvedCommentsLeft((value) => !value)
                  : setShowResolvedCommentsRight((value) => !value)
              }
            />
          </CommentsSection>
        </ComaparisonModeWrapper>
      </WorkspaceWrapper>
    );
  }

  return (
    <LoadingContainer>
      <Loading size="large" />
    </LoadingContainer>
  );
};
